import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Injectable } from '@angular/core';
import { BaseEntity } from '../entities/base.entity';
import { makeQueryKey } from '../../utils/entity-states/query-key';

export interface EntityStateModel<T extends BaseEntity> {
  entities: Record<string, T>
  ids: string[]
  loadedQueries: Record<string, boolean | undefined>
  loadedQueriesMap?: Record<string, string[]>
  loadedIds: Record<string, boolean | undefined>
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  meta?: Record<string, any>
}

export class LoadMany {
  static readonly type = '[Entity] Load Many';
  constructor(
    public payload: {
      entities: Record<string, BaseEntity>
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      query?: Record<string, any>
      stateName: string
      relationKeys?: string[]
    }
  ) {}
}

export class LoadSingle {
  static readonly type = '[Entity] Load Single';

  constructor(
    public payload: {
      entity: BaseEntity
      id: string
      stateName: string
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      query?: Record<string, any>
      relationKeys?: string[]
    }
  ) {}
}

export class Create {
  static readonly type = '[Entity] Create';
  constructor(public payload: BaseEntity) {}
}

export class Update {
  static readonly type = '[Entity] Update';
  constructor(public payload: BaseEntity) {}
}

export class Delete {
  static readonly type = '[Entity] Delete';
  constructor(public id: string) {}
}

export class InvalidateLoadedQueries {
  static readonly type = '[Entity] Invalidate Loaded Queries';
}

export class StoreMeta {
  static readonly type = '[Entity] Store Meta';
  constructor(
    public queryKey: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    public meta: any,
    public stateName: string
  ) {}
}

export interface GenericEntityStateClass<T extends BaseEntity> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  new(): any
  all(state: EntityStateModel<T>): T[]
  byId(state: EntityStateModel<T>): (id: string) => T | undefined
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  allByQuery(state: EntityStateModel<T>, props: { query: Record<string, any> }): T[]
}

// Generic state factory
export function createEntityState<T extends BaseEntity>(name: string) {
  @State<EntityStateModel<T>>({
    name,
    defaults: {
      entities: {} as Record<string, T>,
      ids: [] as string[],
      loadedQueries: {} as Record<string, boolean | undefined>,
      loadedQueriesMap: {} as Record<string, string[]>,
      loadedIds: {} as Record<string, boolean | undefined>,
    },
  })
  @Injectable()
  class GenericEntityState {
    static stateName = name;

    // Selector for all entities
    @Selector()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static all(state: EntityStateModel<any>): any[] {
      if (!state?.ids || !state.entities) return [];
      return state.ids.map(id => state.entities[id]);
    }

    @Selector()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static allByQuery(state: EntityStateModel<any>, props?: { query: Record<string, any> }): any[] {
      if (!props?.query) return [];
      const queryKey = makeQueryKey(props.query);
      const ids = state.loadedQueriesMap?.[queryKey] ?? [];
      return ids
        .map(id => state.entities[id])
        .filter((entity): entity is BaseEntity => !!entity);
    }

    /** Selector: entity by id */
    @Selector()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    static byId(state: EntityStateModel<any>) {
      return (id: string) => state?.entities?.[id];
    }

    @Action(LoadSingle)
    loadSingle(
      ctx: StateContext<EntityStateModel<T>>,
      { payload }: LoadSingle
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if ((GenericEntityState as any).stateName !== payload.stateName) return;

      const state = ctx.getState();
      const { entity, id, query } = payload;

      const queryKey = query ? makeQueryKey(query) : undefined;
      const loadedIdKey = queryKey ? `${id}:${queryKey}` : id;

      ctx.patchState({
        entities: {
          ...state.entities,
          [id]: entity as T,
        },
        ids: state.ids.includes(id) ? state.ids : [...state.ids, id],
        loadedIds: {
          ...state.loadedIds,
          [loadedIdKey]: true,
        },
      });
    }

    // === ACTIONS ===
    @Action(LoadMany)
    loadMany(ctx: StateContext<EntityStateModel<T>>, action: LoadMany) {
      // Only handle actions for this state
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (action.payload.stateName !== (GenericEntityState as any).stateName) return;

      const state = ctx.getState();
      const ids = Object.keys(action.payload.entities);
      const queryKey = makeQueryKey(action.payload.query || {});

      // Track loaded IDs
      const newLoadedIds = { ...state.loadedIds };
      ids.forEach((id) => (newLoadedIds[id] = true));

      // Relation-safe merge
      const mergedEntities: Record<string, T> = { ...state.entities };
      const relationKeys: string[] = action.payload.relationKeys ?? [];

      for (const [id, incoming] of Object.entries(action.payload.entities)) {
        const existing = mergedEntities[id];

        if (!existing) {
          // New entity, just store it
          mergedEntities[id] = incoming as T;
          continue;
        }

        // Preserve missing relations from existing entity
        const preservedRelations: Partial<T> = {};
        for (const key of relationKeys) {
          if (!(key in incoming) && key in existing) {
            preservedRelations[key as keyof T] = existing[key as keyof T];
          }
        }

        // Merge incoming data with existing, preserving relations
        mergedEntities[id] = {
          ...existing,
          ...incoming,
          ...preservedRelations,
        } as T;
      }

      // Patch state
      ctx.patchState({
        entities: mergedEntities,
        ids: [...new Set([...state.ids, ...ids])],
        loadedQueries: { ...state.loadedQueries, [queryKey]: true },
        loadedQueriesMap: { ...state.loadedQueriesMap, [queryKey]: ids },
        loadedIds: newLoadedIds,
      });
    }

    @Action(Create)
    create(ctx: StateContext<EntityStateModel<T>>, action: Create) {
      const state = ctx.getState();
      const entity = action.payload as T;
      const id = entity.id;

      ctx.patchState({
        entities: { ...state.entities, [id]: entity },
        ids: [...new Set([...state.ids, id])],
        loadedIds: { ...state.loadedIds, [id]: true },
        loadedQueriesMap: { ...state.loadedQueriesMap },
      });
    }

    @Action(Update)
    update(ctx: StateContext<EntityStateModel<T>>, action: Update) {
      const state = ctx.getState();
      const entity = action.payload as T;
      const id = entity.id;

      ctx.patchState({
        entities: { ...state.entities, [id]: entity },
        ids: state.ids.includes(id) ? state.ids : [...state.ids, id],
        loadedIds: { ...state.loadedIds, [id]: true },
        loadedQueries: { ...state.loadedQueries },
      });
    }

    @Action(Delete)
    delete(ctx: StateContext<EntityStateModel<T>>, action: Delete) {
      const state = ctx.getState();
      const id = action.id;

      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [id]: _, ...remainingEntities } = state.entities || {};

      ctx.patchState({
        entities: remainingEntities as Record<string, T>,
        ids: state.ids?.filter(existingId => existingId !== id) ?? [],
        loadedIds: { ...state.loadedIds, [id]: false },
        loadedQueries: { ...state.loadedQueries },
      });
    }

    @Action(InvalidateLoadedQueries)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    invalidateLoadedQueries(ctx: StateContext<EntityStateModel<any>>) {
      const state = ctx.getState();
      const newLoadedQueries = { ...state.loadedQueries };

      for (const key of Object.keys(newLoadedQueries)) {
        if (newLoadedQueries[key]) {
          newLoadedQueries[key] = false; // mark only loaded queries as stale
        }
      }

      ctx.patchState({ loadedQueries: newLoadedQueries });
    }

    @Action(StoreMeta)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    storeMeta(ctx: StateContext<EntityStateModel<any>>, action: StoreMeta) {
      const state = ctx.getState();

      ctx.patchState({
        meta: {
          ...(state.meta || {}),
          [action.queryKey]: action.meta
        }
      });
    }
  }

  return GenericEntityState;
}
